<div
  [showIfHasCapability]="[
    {
      capabilityString: 'CanAccessOrdersReport',
      type: 'system'
    }
  ]"
class="content85percent capabilities-margin">

<div class="page-header-bar mt-3">
  <div>
    <h1>Orders Report</h1>
    <h5 class="selected-date-display">
      {{ getFormattedSelectedDate() }}@if (isToday()) {
      <span> (Today)</span>
    }
  </h5>
</div>
<div class="float-end">
  <div class="d-flex align-items-center">
    <div class="date-selector-section me-3">
      <span class="me-2">Change Date:</span>
      <input
        matInput
        [matDatepicker]="datePicker"
        [formControl]="dateControl"
        (dateChange)="onDateSelected($event)"
        readonly
        class="hidden-input">
        <mat-datepicker-toggle [for]="datePicker">
          <mat-icon matDatepickerToggleIcon svgIcon="calendar"></mat-icon>
        </mat-datepicker-toggle>
        <mat-datepicker #datePicker></mat-datepicker>
      </div>

      @if (isToday()) {
        <mat-slide-toggle
          class="me-3"
          [(ngModel)]="autoRefreshEnabled"
          (change)="toggleAutoRefresh()"
          [matTooltip]="'Auto-refresh data every 1 minute for live monitoring'"
          labelPosition="before">
          <span class="mat-slide-toggle-content">
            Auto-Refresh
          </span>
        </mat-slide-toggle>
        <button
          type="button"
          class="btn btn-primary"
          [disabled]="isCopyingEmails"
          (click)="onCopySigningAgentEmails()"
          title="Copies signing agent emails from Ready and Active orders">
          @if (isCopyingEmails) {
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          }
          @if (!isCopyingEmails) {
            <fa-icon [icon]="faCopy"></fa-icon>
          }
          {{ isCopyingEmails ? 'Copying...' : 'Copy Signing Agent Emails' }}
        </button>
      }
    </div>
  </div>
</div>

<!-- Note about data filtering -->
<p class="text-muted mb-3">
  <strong>Note:</strong> All values are based on orders scheduled for the selected date.
</p>

<!-- Order Summary Cards -->
@if (orderSummary$ | async; as summary) {
  <div class="row mb-4">
    <div class="col">
      <div class="card report-card complete-card clickable-card"
        [class.selected-tile]="selectedTile === 'ready'"
        (click)="selectTile('ready')"
        role="button"
        tabindex="0"
        (keydown.enter)="selectTile('ready')"
        (keydown.space)="selectTile('ready')">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalReady }}</h4>
              <p class="card-text">Ready</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faCalendar" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card report-card info-card clickable-card"
        [class.selected-tile]="selectedTile === 'active'"
        (click)="selectTile('active')"
        role="button"
        tabindex="0"
        (keydown.enter)="selectTile('active')"
        (keydown.space)="selectTile('active')"
        [matTooltip]="'In Progress: ' + summary.inProgressCount + '\nSigning: ' + summary.signingCount + '\nSession Complete: ' + summary.sessionCompleteCount + '\nVaulting: ' + summary.vaultingCount"
        matTooltipClass="status-breakdown-tooltip"
        matTooltipPosition="above">
        <div class="card-body">
          <small class="text-muted hover-hint">* Hover for breakdown</small>
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalActive }}</h4>
              <p class="card-text">Active: In Progress, Signing, Session Complete, Vaulting</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faPen" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card report-card ready-card clickable-card"
        [class.selected-tile]="selectedTile === 'complete'"
        (click)="selectTile('complete')"
        role="button"
        tabindex="0"
        (keydown.enter)="selectTile('complete')"
        (keydown.space)="selectTile('complete')">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalComplete }}</h4>
              <p class="card-text">Complete</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faCheckCircle" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card report-card cancelled-card clickable-card"
        [class.selected-tile]="selectedTile === 'cancelled'"
        (click)="selectTile('cancelled')"
        role="button"
        tabindex="0"
        (keydown.enter)="selectTile('cancelled')"
        (keydown.space)="selectTile('cancelled')">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalCancelled }}</h4>
              <p class="card-text">Cancelled</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faUserXmark" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col">
      <div class="card report-card error-card clickable-card"
        [class.selected-tile]="selectedTile === 'unsuccessful'"
        (click)="selectTile('unsuccessful')"
        role="button"
        tabindex="0"
        (keydown.enter)="selectTile('unsuccessful')"
        (keydown.space)="selectTile('unsuccessful')"
        [matTooltip]="'Signing Failed: ' + summary.signingFailedCount + '\nOpt Out: ' + summary.optOutCount + '\nVaulting Failed: ' + summary.vaultingFailedCount"
        matTooltipClass="status-breakdown-tooltip"
        matTooltipPosition="above">
        <div class="card-body">
          <small class="text-muted hover-hint">* Hover for breakdown</small>
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalUnsuccessful }}</h4>
              <p class="card-text">Unsuccessful: Opt Out, Signing Failed, Vaulting Failed</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faExclamationTriangle" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
}

<!-- Table Filters -->
@if (orderSummary$ | async) {
  <div class="card mb-3">
    <div class="card-body py-2">
      <div class="row g-2 align-items-end">
        <div class="col">
          <label class="form-label mb-1 small fw-semibold">Order ID</label>
          <input type="text" class="form-control form-control-sm" [(ngModel)]="tableFilters.orderId" (ngModelChange)="onFilterChange()" placeholder="Filter...">
        </div>
        <div class="col">
          <label class="form-label mb-1 small fw-semibold">Loan ID</label>
          <input type="text" class="form-control form-control-sm" [(ngModel)]="tableFilters.loanId" (ngModelChange)="onFilterChange()" placeholder="Filter...">
        </div>
        <div class="col">
          <label class="form-label mb-1 small fw-semibold">3rd Party ID</label>
          <input type="text" class="form-control form-control-sm" [(ngModel)]="tableFilters.thirdPartyId" (ngModelChange)="onFilterChange()" placeholder="Filter...">
        </div>
        <div class="col">
          <label class="form-label mb-1 small fw-semibold">Package GUID</label>
          <input type="text" class="form-control form-control-sm" [(ngModel)]="tableFilters.packageGuid" (ngModelChange)="onFilterChange()" placeholder="Filter...">
        </div>
        <div class="col">
          <label class="form-label mb-1 small fw-semibold">Order Type</label>
          <mat-select
            class="form-control form-control-sm order-type-select"
            [multiple]="true"
            placeholder="All"
            [(ngModel)]="tableFilters.orderType"
            (ngModelChange)="onFilterChange()">
            @for (type of orderTypeOptions; track type.key) {
              <mat-option [value]="type.key">{{ type.label }}</mat-option>
            }
          </mat-select>
        </div>
        <div class="col-auto">
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearFilters()" [style.visibility]="hasActiveFilters() ? 'visible' : 'hidden'">Clear Filters</button>
        </div>
      </div>
    </div>
  </div>
}

<!-- Ready Orders Table -->
@if (selectedTile === 'ready' && orderSummary$ | async) {
  <div class="card">
    <div class="card-header">
      <h3 class="mb-0">Ready Orders</h3>
    </div>
    <div class="card-body">
      @if (readyOrders$ | async; as orders) {
        <div>
          <ng-container *ngTemplateOutlet="simpleOrdersTable; context: { orders: filterOrders(orders), emptyMessage: 'No ready orders found.' }"></ng-container>
        </div>
      } @else {
        <div class="text-center py-4">
          <div class="spinner-border text-dark" role="status">
            <span class="visually-hidden">Loading orders...</span>
          </div>
          <p class="mt-2 text-muted">Loading orders...</p>
        </div>
      }
    </div>
  </div>
}

<!-- Active Orders Details -->
@if (selectedTile === 'active' && orderSummary$ | async) {
  <div class="card">
    <div class="card-header">
      <h3 class="mb-0">Active Orders</h3>
    </div>
    <div class="card-body">
      @if (inProgressOrders$ | async; as orders) {
        <div>
          @if (orders.length === 0) {
            <div class="text-center py-4">
              <p class="text-muted">No active orders found.</p>
            </div>
          }
          @if (filterOrders(orders); as filteredOrders) {
            @if (filteredOrders.length === 0 && orders.length > 0) {
              <div class="text-center py-4">
                <p class="text-muted">No active orders match the current filters.</p>
              </div>
            }
            @for (order of filteredOrders; track order) {
              <div class="mb-3 border rounded">
                <!-- Collapsed Summary View -->
                <button
                  type="button"
                  class="order-summary-toggle w-100 text-start p-3"
                  (click)="toggleOrderExpansion(order.orderId)"
                  [attr.aria-expanded]="isOrderExpanded(order.orderId)"
                  [attr.aria-controls]="'order-details-' + order.orderId">
                  <div class="row align-items-center">
                    <div class="col-md-12">
                      <div class="d-flex align-items-center mb-2">
                        <fa-icon
                          [icon]="isOrderExpanded(order.orderId) ? faChevronUp : faChevronDown"
                          class="me-2 text-muted">
                        </fa-icon>
                        <h6 class="mb-0">
                          Order: <a [routerLink]="['/order-detail', order.orderId]" target="_blank" class="nav-link d-inline" [title]="order.orderId" (click)="$event.stopPropagation()">{{ order.orderId }}</a>&nbsp;<button type="button" class="btn-copy-inline" title="Copy" (click)="$event.stopPropagation(); copyToClipboard(order.orderId.toString())"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button> | Loan: <span [title]="order.loanIdentifier">{{ order.loanIdentifier }}</span>&nbsp;
                          @if (order.loanIdentifier) {
                            <button type="button" class="btn-copy-inline" title="Copy" (click)="$event.stopPropagation(); copyToClipboard(order.loanIdentifier)"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                          } | 3rd Party: <span [title]="order.thirdPartyOrderId">{{ order.thirdPartyOrderId }}</span>&nbsp;
                          @if (order.thirdPartyOrderId && order.thirdPartyOrderId !== 'N/A') {
                            <button type="button" class="btn-copy-inline" title="Copy" (click)="$event.stopPropagation(); copyToClipboard(order.thirdPartyOrderId)"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                          } | GUID: <span>{{ order.packageGuid }}</span>&nbsp;@if (order.packageGuid) {
                            <button type="button" class="btn-copy-inline" title="Copy" (click)="$event.stopPropagation(); copyToClipboard(order.packageGuid)"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                          }
                      </h6>
                    </div>
                    <div class="row">
                      <div class="col-sm-2">
                        <small><strong>Client:</strong><br>
                        <span class="text-truncate d-inline-block" style="max-width: 100%;" [title]="order.clientName">{{ order.clientName }}</span>
                      </small>
                    </div>
                    <div class="col-sm-1">
                      <small><strong>Type:</strong><br>
                        <span class="text-truncate d-inline-block" style="max-width: 100%;" [title]="order.orderType">{{ order.orderType }}</span>
                      </small>
                    </div>
                    <div class="col-sm-2">
                      <small><strong>Status:</strong><br>
                        <span [title]="order.statusCode">{{ order.statusCode }}</span>
                      </small>
                    </div>
                    <div class="col-sm-3">
                      <small><strong>Signing Agent:</strong><br>
                        @if (order.signingAgentEmail) {
                          <span [title]="order.signingAgentEmail">
                            {{ order.signingAgentEmail }}
                          </span>
                        }
                        @if (!order.signingAgentEmail) {
                          <span class="text-muted" title="None assigned">
                            None assigned
                          </span>
                        }
                      </small>
                    </div>
                    <div class="col-sm-2">
                      <small><strong>Scheduled:</strong> <mat-icon class="tooltip-icon" style="height: 1.1rem; width: .9rem;" matTooltip="Time is based on the property address' time zone." matTooltipPosition="below" matTooltipShowDelay="500" svgIcon="information"></mat-icon><br>
                        <span [title]="formatDateTime(order.scheduledDateTime)">{{ formatDateTime(order.scheduledDateTime) }}</span>
                      </small>
                    </div>
                    <div class="col-sm-2">
                      <small><strong>Updated:</strong><br>
                        @if (order.statusUpdatedDate) {
                          <span [title]="formatUpdatedDateTime(order.statusUpdatedDate)">{{ formatUpdatedDateTime(order.statusUpdatedDate) }}</span>
                        }
                        @if (!order.statusUpdatedDate) {
                          <span title="N/A">N/A</span>
                        }
                      </small>
                    </div>
                  </div>
                </div>
      </div>
  </button>
  <!-- Expanded Detailed View -->
  @if (isOrderExpanded(order.orderId)) {
    <div
      class="border-top p-3"
      [id]="'order-details-' + order.orderId">
      <!-- Order Progress Tracker -->
      <div class="mb-4">
        <h6 class="mb-3">Order Progress</h6>
        <div class="progress-tracker">
          <div class="progress-step ready-step" [class.completed]="getStepCompleted('READY', order.statusCode)" [class.current]="order.statusCode === 'READY'">
            <div class="step-circle">
              @if (getStepCompleted('READY', order.statusCode) || order.statusCode === 'READY') {
                <fa-icon [icon]="faCalendar"></fa-icon>
              }
            </div>
            <div class="step-label">Ready</div>
          </div>
          <div class="progress-line" [class.completed]="['INPROGRESS','SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['INPROGRESS','SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)"></div>
          <div class="progress-step active-step" [class.completed]="getStepCompleted('INPROGRESS', order.statusCode)" [class.current]="order.statusCode === 'INPROGRESS'">
            <div class="step-circle">
              @if (getStepCompleted('INPROGRESS', order.statusCode) || order.statusCode === 'INPROGRESS') {
                <fa-icon [icon]="faPen"></fa-icon>
              }
            </div>
            <div class="step-label">In Progress</div>
          </div>
          <div class="progress-line" [class.completed]="['SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)"></div>
          <div class="progress-step active-step" [class.completed]="getStepCompleted('SIGNING', order.statusCode)" [class.current]="order.statusCode === 'SIGNING'">
            <div class="step-circle">
              @if (getStepCompleted('SIGNING', order.statusCode) || order.statusCode === 'SIGNING') {
                <fa-icon [icon]="faPen"></fa-icon>
              }
            </div>
            <div class="step-label">Signing</div>
          </div>
          <div class="progress-line" [class.completed]="['SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)"></div>
          <div class="progress-step active-step" [class.completed]="getStepCompleted('SESSIONCOMPLETE', order.statusCode)" [class.current]="order.statusCode === 'SESSIONCOMPLETE'">
            <div class="step-circle">
              @if (getStepCompleted('SESSIONCOMPLETE', order.statusCode) || order.statusCode === 'SESSIONCOMPLETE') {
                <fa-icon [icon]="faPen"></fa-icon>
              }
            </div>
            <div class="step-label">Session Complete</div>
          </div>
          <div class="progress-line" [class.completed]="['VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['VAULTING','COMPLETE'].includes(order.statusCode)"></div>
          <div class="progress-step active-step" [class.completed]="getStepCompleted('VAULTING', order.statusCode)" [class.current]="order.statusCode === 'VAULTING'">
            <div class="step-circle">
              @if (getStepCompleted('VAULTING', order.statusCode) || order.statusCode === 'VAULTING') {
                <fa-icon [icon]="faPen"></fa-icon>
              }
            </div>
            <div class="step-label">Vaulting</div>
          </div>
          <div class="progress-line" [class.completed]="order.statusCode === 'COMPLETE'" [class.complete-line]="order.statusCode === 'COMPLETE'"></div>
          <div class="progress-step complete-step" [class.completed]="getStepCompleted('COMPLETE', order.statusCode)" [class.current]="order.statusCode === 'COMPLETE'">
            <div class="step-circle">
              @if (getStepCompleted('COMPLETE', order.statusCode) || order.statusCode === 'COMPLETE') {
                <fa-icon [icon]="faCheckCircle"></fa-icon>
              }
            </div>
            <div class="step-label">Complete</div>
          </div>
        </div>
      </div>
      <!-- Participants and Details Section -->
      <div class="row mt-3">
        <div class="col-md-9">
          <h6>Participants</h6>
          <div class="table-responsive">
            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                @for (participant of order.participants; track participant) {
                  <tr>
                    <td [title]="participant.firstName + ' ' + participant.lastName">{{ participant.firstName }} {{ participant.lastName }}</td>
                    <td [title]="participant.role">{{ participant.role }}</td>
                    <td>
                      @if (participant.email) {
                        <span [title]="participant.email">{{ participant.email }}</span>
                      }
                      @if (!participant.email) {
                        <span class="text-muted" title="No email">No email</span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        <div class="col-md-3 d-flex align-items-center justify-content-center">
          <button
            type="button"
            class="btn-secondary"
            (click)="openAuditLogModal(order)">
            View Audit Log
          </button>
        </div>
      </div>
    </div>
  }
</div>
}
}
</div>
} @else {
  <div class="text-center py-4">
    <div class="spinner-border text-dark" role="status">
      <span class="visually-hidden">Loading orders...</span>
    </div>
    <p class="mt-2 text-muted">Loading orders...</p>
  </div>
}
</div>
</div>
}

<!-- Complete Orders Table -->
@if (selectedTile === 'complete' && orderSummary$ | async) {
  <div class="card">
    <div class="card-header">
      <h3 class="mb-0">Complete Orders</h3>
    </div>
    <div class="card-body">
      @if (completeOrders$ | async; as orders) {
        <div>
          <ng-container *ngTemplateOutlet="simpleOrdersTable; context: { orders: filterOrders(orders), emptyMessage: 'No complete orders found.' }"></ng-container>
        </div>
      } @else {
        <div class="text-center py-4">
          <div class="spinner-border text-dark" role="status">
            <span class="visually-hidden">Loading orders...</span>
          </div>
          <p class="mt-2 text-muted">Loading orders...</p>
        </div>
      }
    </div>
  </div>
}

<!-- Cancelled Orders Table -->
@if (selectedTile === 'cancelled' && orderSummary$ | async) {
  <div class="card">
    <div class="card-header">
      <h3 class="mb-0">Cancelled Orders</h3>
    </div>
    <div class="card-body">
      @if (cancelledOrders$ | async; as orders) {
        <div>
          <ng-container *ngTemplateOutlet="simpleOrdersTable; context: { orders: filterOrders(orders), emptyMessage: 'No cancelled orders found.' }"></ng-container>
        </div>
      } @else {
        <div class="text-center py-4">
          <div class="spinner-border text-dark" role="status">
            <span class="visually-hidden">Loading orders...</span>
          </div>
          <p class="mt-2 text-muted">Loading orders...</p>
        </div>
      }
    </div>
  </div>
}

<!-- Unsuccessful Orders Table -->
@if (selectedTile === 'unsuccessful' && orderSummary$ | async) {
  <div class="card">
    <div class="card-header">
      <h3 class="mb-0">Unsuccessful Orders</h3>
    </div>
    <div class="card-body">
      @if (unsuccessfulOrders$ | async; as orders) {
        <div>
          <ng-container *ngTemplateOutlet="simpleOrdersTable; context: { orders: filterOrders(orders), emptyMessage: 'No unsuccessful orders found.' }"></ng-container>
        </div>
      } @else {
        <div class="text-center py-4">
          <div class="spinner-border text-dark" role="status">
            <span class="visually-hidden">Loading orders...</span>
          </div>
          <p class="mt-2 text-muted">Loading orders...</p>
        </div>
      }
    </div>
  </div>
}


<ng-template #simpleOrdersTable let-orders="orders" let-emptyMessage="emptyMessage">
  @if (orders.length === 0) {
    <div class="text-center py-4">
      <p class="text-muted">{{ emptyMessage }}</p>
    </div>
  }

  @if (orders.length > 0) {
    <div class="table-responsive">
      <table class="table table-striped">
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Loan ID</th>
            <th>3rd Party ID</th>
            <th>Package GUID</th>
            <th>Client</th>
            <th>Type</th>
            <th>Status</th>
            <th>Signing Agent</th>
            <th>Participants</th>
            <th>
              Scheduled
              <mat-icon class="tooltip-icon" style="height: 1rem; width: .9rem;" matTooltip="Time is based on the property address' time zone." matTooltipPosition="below" matTooltipShowDelay="500" svgIcon="information"></mat-icon>
            </th>
            <th>Updated</th>
            <th>Audit Log</th>
          </tr>
        </thead>
        <tbody>
          @for (order of orders; track order) {
            <tr>
              <td [title]="order.orderId">
                <div class="d-flex align-items-center gap-1">
                  <a [routerLink]="['/order-detail', order.orderId]" target="_blank" class="nav-link">{{ order.orderId }}</a>
                  <button type="button" class="btn-copy-inline" title="Copy" (click)="copyToClipboard(order.orderId.toString())"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                </div>
              </td>
              <td [title]="order.loanIdentifier || 'N/A'">
                <div class="d-flex align-items-center gap-1">
                  <span class="text-truncate">{{ order.loanIdentifier || 'N/A' }}</span>
                  @if (order.loanIdentifier) {
                    <button type="button" class="btn-copy-inline flex-shrink-0" title="Copy" (click)="copyToClipboard(order.loanIdentifier)"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                  }
                </div>
              </td>
              <td [title]="order.thirdPartyOrderId">
                <div class="d-flex align-items-center gap-1">
                  <span class="text-truncate">{{ order.thirdPartyOrderId }}</span>
                  @if (order.thirdPartyOrderId && order.thirdPartyOrderId !== 'N/A') {
                    <button type="button" class="btn-copy-inline flex-shrink-0" title="Copy" (click)="copyToClipboard(order.thirdPartyOrderId)"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                  }
                </div>
              </td>
              <td>
                <div class="d-flex align-items-center gap-1">
                  <span class="text-truncate" [title]="order.packageGuid">{{ order.packageGuid }}</span>
                  @if (order.packageGuid) {
                    <button type="button" class="btn-copy-inline flex-shrink-0" title="Copy" (click)="copyToClipboard(order.packageGuid)"><fa-icon [icon]="faCopy" size="1x"></fa-icon></button>
                  }
                </div>
              </td>
              <td [title]="order.clientName">{{ order.clientName }}</td>
              <td [title]="order.orderType">{{ order.orderType }}</td>
              <td [title]="order.statusCode">{{ order.statusCode }}</td>
              <td>
                @if (order.participants | signingAgentFilter; as signingAgents) {
                  @if (signingAgents.length > 0 && signingAgents[0].email) {
                    <span [title]="signingAgents[0].email">{{ signingAgents[0].email }}</span>
                  }
                  @if (signingAgents.length === 0 || !signingAgents[0].email) {
                    <span title="N/A">N/A</span>
                  }
                }
              </td>
              <td>
                @if (order.participants | otherParticipantsFilter; as others) {
                  @if (others.length > 0) {
                    <span [title]="others | participantNames">{{ others | participantNames }}</span>
                  }
                  @if (others.length === 0) {
                    <span title="N/A">N/A</span>
                  }
                }
              </td>
              <td [title]="formatDateTime(order.scheduledDateTime)">{{ formatDateTime(order.scheduledDateTime) }}</td>
              <td>
                @if (order.statusUpdatedDate) {
                  <span [title]="formatUpdatedDateTime(order.statusUpdatedDate)">{{ formatUpdatedDateTime(order.statusUpdatedDate) }}</span>
                }
                @if (!order.statusUpdatedDate) {
                  <span title="N/A">N/A</span>
                }
              </td>
              <td><a class="nav-link" style="cursor: pointer;" title="View Audit Log" (click)="openAuditLogModal(order)">View</a></td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</ng-template>
</div>
